import React from 'react'
import {
  Accordion,
  AccordionItem,
  Space,
  Box,
  TextBody,
} from '@ally/metronome-ui'
import { AccountLOB, AccountType, Account } from '@ally/data-types-afg-accounts'
import { uniq } from 'ramda'

import { useCustomizeDashboard } from '../CustomizeDashboardContext/CustomizeDashboardContext'
import ReorderList from '../ReorderList'
import { DataTypeName, DataType, AccountTypesObj } from '../types'
import { useAccountTypes } from '../../providers'
import { reorder } from '../utils'

/**
 * Returns the `accountType`
 */
function mapAccountType(account: Account): string {
  if (account.indicator?.retirement) return 'IRA'
  return account.type
}

/**
 * Filters out any non-visible DEPOSIT accounts.
 *
 * Non-visible deposit accounts can exist in a customization response when a deposit
 * account they customized has closed and it has been more than 60 days. We need to
 * purge that closed account type so when they save again it is no longer present.
 */
function filterBank(
  visibleDepositAccountTypes: string[],
  accountName: string,
): boolean {
  // 'CHECKING' is used in `accountComponents` instead of 'DDA'
  if (accountName === 'CHECKING') {
    return visibleDepositAccountTypes.includes(AccountType.DDA)
  }

  // There are multiple types of savings accounts
  if (accountName === 'SAVINGS') {
    return [
      AccountType.SDA,
      AccountType.MMA,
      AccountType.CMG,
      // IRA is considered a savings account but it's a SDA with indicator.retirement === true
      'IRA',
    ].some(accountType => visibleDepositAccountTypes.includes(accountType))
  }

  return visibleDepositAccountTypes.includes(accountName)
}

const Accounts: React.FC<{
  onListChange: (type: DataTypeName, list: DataType<DataTypeName>[]) => void
}> = ({ onListChange }) => {
  const [{ customizeApiData, customizeDataToPatch }] = useCustomizeDashboard()
  const {
    activeDepositAccounts,
    investAccounts: allInvestAccounts,
    allCCAccounts,
  } = useAccountTypes()

  const visibleDepositAccountTypes = uniq(
    activeDepositAccounts.map(mapAccountType),
  )
    // `accountComponents` uses CD as a key instead of CDA
    .map(name => (name === 'CDA' ? 'CD' : name))

  const visibleInvestAccountTypes = uniq(allInvestAccounts.map(mapAccountType))
  const visibleCreditCardAccountTypes = uniq(allCCAccounts.map(acc => acc.type))

  const accountTypes =
    customizeDataToPatch.accountTypes ?? customizeApiData.accountTypes

  if (accountTypes.length < 1) return null

  const { lobs: customizationLobs } = customizeApiData

  const showAccordion = customizationLobs.length > 1

  const investAccounts = accountTypes
    .filter(
      account =>
        account.lob === 'INVEST' &&
        visibleInvestAccountTypes.includes(account.name),
    )
    .map(reorder)

  const bankAccounts = accountTypes
    .filter(
      account =>
        account.lob === 'DEPOSIT' &&
        filterBank(visibleDepositAccountTypes, account.name),
    )
    .map(reorder)

  const creditCardAccounts = accountTypes.filter(
    account =>
      account.lob === 'CC' &&
      visibleCreditCardAccountTypes.includes(account.name as AccountType),
  )

  const hasMultipleInvestAccounts = investAccounts.length > 1
  const hasMultipleBankAccounts = bankAccounts.length > 1
  const hasMultipleCreditCardAccounts = creditCardAccounts.length > 1

  const getAccordionItem = (
    heading: string,
    accounts: AccountTypesObj[],
  ): AccordionItem => ({
    heading,
    panel: (
      <ReorderList
        type="accountTypes"
        list={accounts}
        onListChange={onListChange}
      />
    ),
  })

  const lobToAccordionItem: { [key: string]: AccordionItem | false } = {
    [AccountLOB.INVEST]:
      hasMultipleInvestAccounts &&
      getAccordionItem('Order Investment Accounts', investAccounts),
    [AccountLOB.DEPOSIT]:
      hasMultipleBankAccounts &&
      getAccordionItem('Order Bank Accounts', bankAccounts),
    [AccountLOB.CC]:
      hasMultipleCreditCardAccounts &&
      getAccordionItem('Order Credit Card Accounts', creditCardAccounts),
  }

  const accordionItems = (): AccordionItem[] => {
    return customizationLobs
      .map(({ name }) => lobToAccordionItem[name])
      .filter(Boolean) as AccordionItem[]
  }

  return (
    <>
      {hasMultipleInvestAccounts ||
      hasMultipleBankAccounts ||
      hasMultipleCreditCardAccounts ? (
        <Box>
          <Space mb="sm">
            <TextBody tag="p" size="sm" weight="bold">
              How would you like to order your accounts?
            </TextBody>
          </Space>
          <Box>
            {showAccordion && (
              <Accordion
                disableContainer
                data-testid="order-account-types-accordion"
                items={accordionItems()}
              />
            )}

            {hasMultipleInvestAccounts && !showAccordion && (
              <ReorderList
                type="accountTypes"
                list={investAccounts}
                onListChange={onListChange}
              />
            )}

            {hasMultipleBankAccounts && !showAccordion && (
              <ReorderList
                type="accountTypes"
                list={bankAccounts}
                onListChange={onListChange}
              />
            )}

            {hasMultipleCreditCardAccounts && !showAccordion && (
              <ReorderList
                type="accountTypes"
                list={creditCardAccounts}
                onListChange={onListChange}
              />
            )}
          </Box>
        </Box>
      ) : null}
    </>
  )
}

export default Accounts
